import { groupBy, mapValues } from "lodash-es";
import { rgba } from "polished";
import React, { FC } from "react";
import { useSelector } from "react-redux";

import { MemberName } from "Bands/components";
import { eventBlockSelectorCreator } from "Bands/state";
import {
  EventUniqueBlocks,
  IEventPeopleBlock,
  IPeopleBlockUser,
  PeopleBlockStatus,
} from "Bands/types";
import { getPeopleBlockStatus } from "Bands/utils";
import { copy } from "shared/assets";
import { Message, Title } from "shared/components";
import { Spacer } from "shared/layout";
import { FlexContainer, Icon, MutedDarkText, TinyText } from "shared/styles";
import { getStatus } from "shared/styles/defaults";
import { NotFlexSpacer } from "../EventSchedule/styles";
import { StyledEventPeopleBlock } from "./style";

interface EventPeopleBlockProps {
  blockId: string;
}

export const EventPeopleBlock: FC<EventPeopleBlockProps> = ({ blockId }) => {
  const peopleBlockSelector = eventBlockSelectorCreator(
    blockId,
    EventUniqueBlocks.people
  );
  const peopleBlock = useSelector(peopleBlockSelector) as IEventPeopleBlock;
  const { peopleBlock: peopleBlockCopy } = copy;

  const usersByStatus = groupBy(peopleBlock?.users, "status") as {
    [key in PeopleBlockStatus]: IPeopleBlockUser[];
  };

  const nbUsersByStatus = mapValues(usersByStatus, (users) => users.length);

  const usersByStatusByCategory = mapValues(
    usersByStatus,
    (users) =>
      groupBy(users, "user.category.label") as {
        [key: string]: IPeopleBlockUser[];
      }
  );

  const users = Object.keys(usersByStatusByCategory);

  return (
    <NotFlexSpacer size="m">
      {!users.length && <Message message={peopleBlockCopy.table.empty} />}
      {users.map((status) => {
        const { icon, iconStatus, viewLabel } =
          getPeopleBlockStatus(status as PeopleBlockStatus) || {};
        const { iconColor = "" } = getStatus(iconStatus);

        const currentStatusUsers =
          usersByStatusByCategory[status as PeopleBlockStatus];

        return (
          <StyledEventPeopleBlock size="s" key={status}>
            <FlexContainer>
              <Icon
                padding="4px"
                isCircle
                size="xxxl"
                as={icon}
                color={iconColor}
                backgroundColor={rgba(iconColor, 0.2)}
              />
              <span>
                <FlexContainer align="flex-end">
                  <Title asComponent="h3" size="s" title={viewLabel} />
                  <MutedDarkText as={TinyText}>
                    • {nbUsersByStatus[status as PeopleBlockStatus]}
                  </MutedDarkText>
                </FlexContainer>
              </span>
            </FlexContainer>
            {Object.keys(currentStatusUsers).map((category) => (
              <Spacer size="xxs" key={category}>
                <TinyText as={MutedDarkText}>
                  {category === "undefined" ? "No category" : category}
                </TinyText>
                <div>
                  {currentStatusUsers[category].map(({ user }, index) => (
                    <React.Fragment key={user.id}>
                      <MemberName user={user} />
                      {currentStatusUsers[category].length - 1 > index && ", "}
                    </React.Fragment>
                  ))}
                </div>
              </Spacer>
            ))}
          </StyledEventPeopleBlock>
        );
      })}
    </NotFlexSpacer>
  );
};
